import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { createFirebaseApp } from "@/lib/firebaseApps";
import { isFirebaseConfigured } from "@/lib/firebase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const CATEGORIES = ["Games", "Productivity", "Social", "Tools", "Education", "Entertainment", "Lifestyle", "Finance"];

export default function AdminAppForm() {
  const navigate = useNavigate();
  const [f, setF] = useState({
    title: "", short_description: "", description: "", category: "Games",
    price_mmk: 0, version: "1.0.0", size_mb: 10, platform: "android",
    developer: "", featured: false, icon_url: "",
  });
  const [iconFile, setIconFile] = useState(null);
  const [appFile, setAppFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, String(v)));
      if (iconFile) fd.append("icon_file", iconFile);
      if (appFile) fd.append("app_file", appFile);
      if (isFirebaseConfigured) {
        await createFirebaseApp({ app: f, iconFile, appFile });
      } else {
        await api.post("/admin/apps", fd);
      }
      toast.success("App အသစ်ဖန်တီးပြီးပါပြီ!");
      navigate("/admin");
    } catch (e) {
      console.error("Create app failed", e);
      toast.error(e?.response?.data?.detail || e?.message || "မအောင်မြင်ပါ");
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="text-sm tracking-wide text-zinc-500 font-medium">Admin · App အသစ်</div>
      <h1 className="text-4xl font-bold tracking-tight">App အသစ်ထည့်ရန်</h1>

      <form onSubmit={submit} className="mt-8 space-y-5 bg-white border border-zinc-200 rounded-3xl p-8">
        <div>
          <Label>ခေါင်းစဉ်</Label>
          <Input data-testid="form-title" required value={f.title} onChange={set("title")} className="h-11 mt-1.5" />
        </div>
        <div>
          <Label>အကျဉ်းချုပ်ဖော်ပြချက် (၁ ကြောင်း)</Label>
          <Input data-testid="form-short" required value={f.short_description} onChange={set("short_description")} className="h-11 mt-1.5" />
        </div>
        <div>
          <Label>အပြည့်အစုံ ဖော်ပြချက်</Label>
          <Textarea data-testid="form-desc" required value={f.description} onChange={set("description")} className="mt-1.5 min-h-32" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>အမျိုးအစား</Label>
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="form-category" className="h-11 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Platform</Label>
            <Select value={f.platform} onValueChange={(v) => setF({ ...f, platform: v })}>
              <SelectTrigger data-testid="form-platform" className="h-11 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="android">Android (.apk)</SelectItem>
                <SelectItem value="ios">iOS (.ipa)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>စျေးနှုန်း (ကျပ်)</Label>
            <Input data-testid="form-price" type="number" min={0} value={f.price_mmk} onChange={(e) => setF({ ...f, price_mmk: Number(e.target.value) })} className="h-11 mt-1.5" />
          </div>
          <div>
            <Label>ဗားရှင်း</Label>
            <Input data-testid="form-version" value={f.version} onChange={set("version")} className="h-11 mt-1.5" />
          </div>
          <div>
            <Label>အရွယ်အစား (MB)</Label>
            <Input data-testid="form-size" type="number" step="0.1" value={f.size_mb} onChange={(e) => setF({ ...f, size_mb: Number(e.target.value) })} className="h-11 mt-1.5" />
          </div>
        </div>

        <div>
          <Label>ဖန်တီးသူ</Label>
          <Input data-testid="form-dev" required value={f.developer} onChange={set("developer")} className="h-11 mt-1.5" />
        </div>

        <div>
          <Label>Icon URL (သို့မဟုတ် အောက်တွင် Upload လုပ်ပါ)</Label>
          <Input data-testid="form-icon-url" value={f.icon_url} onChange={set("icon_url")} className="h-11 mt-1.5" placeholder="https://…" />
        </div>
        <div>
          <Label>Icon ဖိုင် (PNG/JPG)</Label>
          <Input data-testid="form-icon-file" type="file" accept="image/*" onChange={(e) => setIconFile(e.target.files?.[0])} className="h-11 mt-1.5" />
        </div>
        <div>
          <Label>App ဖိုင် (.apk / .ipa / .exe / .dmg)</Label>
          <Input data-testid="form-app-file" type="file" accept=".apk,.ipa,.exe,.dmg,.zip" onChange={(e) => setAppFile(e.target.files?.[0])} className="h-11 mt-1.5" />
          <div className="text-xs text-zinc-500 mt-1">မလိုအပ်ပါ။ နောက်မှလည်း ထည့်နိုင်ပါသည်။</div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Switch data-testid="form-featured" checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} />
          <Label>ပင်မစာမျက်နှာတွင် Feature လုပ်ရန်</Label>
        </div>

        <div className="flex gap-3 pt-4">
          <Button data-testid="form-submit" type="submit" disabled={submitting} className="flex-1 h-12 bg-[#7CC576] hover:bg-[#5FB259] rounded-lg">
            {submitting ? "ဖန်တီးနေသည်…" : "App ဖန်တီးမည်"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/admin")} className="h-12 rounded-lg border-zinc-300">ပယ်ဖျက်</Button>
        </div>
      </form>
    </div>
  );
}
